import React, { useState, useEffect } from 'react';
import { encryptPayload } from './utils/encryption';

function PayloadBuilder() {
  // Load saved data from localStorage
  const loadSavedData = () => {
    try {
      const saved = localStorage.getItem('payloadBuilderData');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.log('Error loading saved data:', error);
    }
    return {
      salt: '',
      useProduction: false,
      customUrl: 'http://localhost:3000',
      useApiMode: true,
      formData: {
        orderId: '',
        documentType: 'both',
        signedBy: '',
        invoiceDate: '',
        contractNumber: '',
        carrier: '',
        prepaid: false,
        remarks: '',
        sailingDate: '',
        storeUrl: '',
        accessToken: '',
        // Weight and packaging settings
        itemsPerCarton: 20,
        packagingWeightMultiplier: 1.1,
        defaultWeight: 0.5,
        displayWeightUnit: 'kg',
        // Payment and shipping terms (payment from API, others with defaults)
        paymentTerms: '', // Will be auto-generated from Shopify payment data
        termsOfPrice: 'FCA, Seoul Republic of Korea',
        portOfLoading: 'Incheon Airport, Republic of Korea',
        // Additional invoice fields
        origin: 'Republic of Korea',
        shipmentTerms: 'Within 2 weeks after receipt of the payment',
        packingTerms: 'Exportable standard packing',
        validity: '3 weeks from Invoice date',
        // Bank details
        bankName: 'SHINHAN BANK (GASANDIGITAL BANKING CENTER)',
        bankAddress: '130, Digital-ro, Geumcheon-gu, Seoul, Korea',
        swiftCode: 'SHBKKRSE',
        accountName: 'PITTASOFT',
        accountNumber: '180-007-8337750',
        remittanceNote: 'All remittance charge must be paid by the sender',
        // Signature details
        closingPhrase: 'Your Very Truly,',
        companySignature: 'Pittasoft Co., Ltd',
        // Shipping origin
        shippingOrigin: 'korea'
      }
    };
  };

  const savedData = loadSavedData();
  
  const [salt, setSalt] = useState(savedData.salt);
  const [useProduction, setUseProduction] = useState(savedData.useProduction);
  const [customUrl, setCustomUrl] = useState(savedData.customUrl);
  const [iframeUrl, setIframeUrl] = useState('');
  const [showPayload, setShowPayload] = useState(false);
  const [encryptedPayload, setEncryptedPayload] = useState('');
  const [documentStatus, setDocumentStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [useApiMode, setUseApiMode] = useState(savedData.useApiMode);
  const [formData, setFormData] = useState(savedData.formData);
  
  // Update fields based on shipping origin
  useEffect(() => {
    if (formData.shippingOrigin === 'korea') {
      setFormData(prev => ({
        ...prev,
        companySignature: 'Pittasoft Co., Ltd',
        termsOfPrice: 'CFR, Republic of Korea',
        portOfLoading: 'Incheon Airport, Republic of Korea',
        origin: 'Republic of Korea',
        paymentTerms: 'by 100% T/T in advance in U.S. dollars'
      }));
    } else if (formData.shippingOrigin === 'usa') {
      setFormData(prev => ({
        ...prev,
        companySignature: 'PITTASOFT AMERICA',
        termsOfPrice: '', // Remove for USA
        portOfLoading: '',
        origin: 'United States',
        paymentTerms: 'by 100% T/T in advance in U.S. dollars'
      }));
    }
  }, [formData.shippingOrigin]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Don't allow changes to auto-set fields
    const autoSetFields = ['paymentTerms', 'termsOfPrice', 'origin', 'companySignature'];
    if (autoSetFields.includes(name)) {
      return; // Ignore changes to auto-set fields
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleShippingOriginChange = (origin) => {
    setFormData(prev => ({
      ...prev,
      shippingOrigin: origin
    }));
  };

  const saveToLocalStorage = () => {
    try {
      const dataToSave = {
        salt,
        useProduction,
        customUrl,
        useApiMode,
        formData
      };
      localStorage.setItem('payloadBuilderData', JSON.stringify(dataToSave));
      console.log('💾 Data saved to localStorage');
    } catch (error) {
      console.log('Error saving to localStorage:', error);
    }
  };

  const generatePayload = () => {
    // Save current data to localStorage before generating
    saveToLocalStorage();
    
    // Set loading state
    setDocumentStatus('loading');
    setErrorMessage('');
    
    const dataWithMode = {
      ...formData,
      useApiMode: useApiMode
    };
    
    if (!useApiMode) {
      dataWithMode.accessToken = '';
    }
    
    const payload = encryptPayload(dataWithMode, salt);
    setEncryptedPayload(payload);
    
    let baseUrl;
    if (useProduction) {
      baseUrl = 'https://shopify-print.lukuku.co/api';
    } else if (customUrl) {
      baseUrl = customUrl.endsWith('/api') ? customUrl : `${customUrl}/api`;
    } else {
      baseUrl = 'http://localhost:3000/api';
    }
    
    const fullUrl = `${baseUrl}/static-document?payload=${encodeURIComponent(payload)}`;
    setIframeUrl(fullUrl);
    setShowPayload(true);
  };

  const handleIframeLoad = (e) => {
    const iframe = e.target;
    
    // Add a small delay to check if content loaded
    setTimeout(() => {
      try {
        // Try to access iframe content to see if it's an error page
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        const bodyText = iframeDoc.body ? iframeDoc.body.innerText || iframeDoc.body.textContent : '';
        
        // Check if it's an error message by looking for error indicators
        // Look for specific error page elements
        const hasErrorContainer = bodyText.includes('error-container');
        const hasErrorTitle = bodyText.includes('error-title');
        const hasTroubleshooting = bodyText.includes('Troubleshooting Steps');
        
        if (hasErrorContainer || hasErrorTitle || hasTroubleshooting ||
            bodyText.includes('Invalid Document Request') || 
            bodyText.includes('Authentication Failed') ||
            bodyText.includes('Permission Denied') ||
            bodyText.includes('Store Not Found') ||
            bodyText.includes('Order Not Found') || 
            bodyText.includes('Missing Credentials') ||
            bodyText.includes('Connection Failed') ||
            bodyText.includes('Document Generation Failed') ||
            bodyText.includes('❌') ||
            bodyText.includes('⚠️') ||
            bodyText.includes('🔐') ||
            bodyText.includes('🚫') ||
            bodyText.includes('🏪') ||
            bodyText.includes('🔍') ||
            bodyText.includes('🌐')) {
          setDocumentStatus('error');
          // Extract error title from HTML
          const titleMatch = bodyText.match(/(Invalid Document Request|Authentication Failed|Permission Denied|Store Not Found|Order Not Found|Missing Credentials|Connection Failed|Document Generation Failed)/);
          setErrorMessage(titleMatch ? titleMatch[0] : 'Document generation failed');
        } else if (bodyText.includes('PACKING LIST') || bodyText.includes('COMMERCIAL INVOICE') || bodyText.includes('PROFORMA INVOICE') || bodyText.includes('BlackVue')) {
          // Document loaded successfully
          setDocumentStatus('success');
        } else if (bodyText.trim() === '') {
          // Empty response
          setDocumentStatus('error');
          setErrorMessage('Empty response from server');
        } else {
          // If we got here with content, assume it's a successful document
          // Don't automatically mark as error for unknown content
          console.log('Document loaded with content, marking as success');
          setDocumentStatus('success');
        }
      } catch (error) {
        // Cross-origin restrictions - check the iframe response status
        // If we can't access content due to CORS, assume success
        console.log('CORS restriction, assuming document loaded');
        setDocumentStatus('success');
      }
    }, 500);
  };

  const handleIframeError = (e) => {
    setDocumentStatus('error');
    setErrorMessage('Failed to load document. Please check your settings and try again.');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  return (
    <div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{ 
        display: 'flex', 
        height: '100vh', 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
      {/* Left Panel - Form */}
      <div style={{ 
        width: '45%', 
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 20px 0 20px',
          flexShrink: 0
        }}>
          <h1 style={{ margin: '0 0 20px 0', fontSize: '24px', color: '#111827' }}>
            🔧 Payload Builder
          </h1>
        </div>
        
        {/* Scrollable Form Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0 20px'
        }}>
          {/* Environment Settings */}
          <div style={{ 
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#374151' }}>
              Environment Settings
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Encryption Salt *
                <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 'normal' }}>
                  {' '}(Never stored in build - Enter manually)
                </span>
              </label>
              <input
                type="password"
                placeholder="Enter encryption salt..."
                value={salt}
                onChange={(e) => setSalt(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                💡 This salt must match the server-side ENCRYPTION_SALT environment variable
              </div>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                <input
                  type="radio"
                  checked={useProduction}
                  onChange={() => setUseProduction(true)}
                  style={{ marginRight: '8px' }}
                />
                Production (shopify-print.lukuku.co)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', marginTop: '8px' }}>
                <input
                  type="radio"
                  checked={!useProduction}
                  onChange={() => setUseProduction(false)}
                  style={{ marginRight: '8px' }}
                />
                Custom URL
              </label>
              {!useProduction && (
                <input
                  type="text"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder="http://localhost:3000"
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    marginTop: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              )}
            </div>
            
            <div style={{ 
              marginBottom: '15px', 
              padding: '10px', 
              backgroundColor: '#f3f4f6',
              borderRadius: '4px',
              border: '1px solid #e5e7eb'
            }}>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Data Mode
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', marginBottom: '8px' }}>
                <input
                  type="radio"
                  checked={useApiMode}
                  onChange={() => setUseApiMode(true)}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ color: '#059669' }}>🔌 API Mode</span> - Fetch real order data from Shopify
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                <input
                  type="radio"
                  checked={!useApiMode}
                  onChange={() => setUseApiMode(false)}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ color: '#dc2626' }}>📝 Dummy Mode</span> - Use sample data (no API call)
              </label>
            </div>
          </div>

          {/* Shipping Origin Selection */}
          <div style={{ 
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#374151' }}>
              Shipping Origin
            </h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => handleShippingOriginChange('korea')}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: formData.shippingOrigin === 'korea' ? '#3b82f6' : '#f3f4f6',
                  color: formData.shippingOrigin === 'korea' ? 'white' : '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: formData.shippingOrigin === 'korea' ? '500' : '400'
                }}
              >
                🇰🇷 Korea (PITTASOFT)
              </button>
              <button
                type="button"
                onClick={() => handleShippingOriginChange('usa')}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: formData.shippingOrigin === 'usa' ? '#3b82f6' : '#f3f4f6',
                  color: formData.shippingOrigin === 'usa' ? 'white' : '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: formData.shippingOrigin === 'usa' ? '500' : '400'
                }}
              >
                🇺🇸 USA (PITTASOFT AMERICA)
              </button>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
              {formData.shippingOrigin === 'korea' 
                ? '✓ Company: Pittasoft Co., Ltd | ✓ Terms of Price: CFR, Republic of Korea'
                : '✓ Company: PITTASOFT AMERICA | ✓ Terms of Price: (Not included)'}
            </div>
          </div>

          {/* Order Information */}
          <div style={{ 
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#374151' }}>
              Order Information
            </h3>
            
            {/* First Row - 2 columns */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                  Order ID *
                </label>
                <input
                  type="text"
                  name="orderId"
                  value={formData.orderId}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                  Document Type
                </label>
                <select
                  name="documentType"
                  value={formData.documentType}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="both">Both Documents</option>
                  <option value="packing">Packing List Only</option>
                  <option value="commercial">Commercial Invoice Only</option>
                </select>
              </div>
            </div>
            
            {/* Second Row - 2 columns */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                  Signed By
                </label>
                <input
                  type="text"
                  name="signedBy"
                  value={formData.signedBy}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                  Invoice Date
                </label>
                <input
                  type="date"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            
            {/* Third Row - 2 columns */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                  Contract Number
                </label>
                <input
                  type="text"
                  name="contractNumber"
                  value={formData.contractNumber}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                  Carrier
                </label>
                <input
                  type="text"
                  name="carrier"
                  value={formData.carrier}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            
            {/* Fourth Row - Checkbox and Date */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', alignItems: 'end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', padding: '8px 0' }}>
                  <input
                    type="checkbox"
                    name="prepaid"
                    checked={formData.prepaid}
                    onChange={handleChange}
                    style={{ marginRight: '8px' }}
                  />
                  Prepaid
                </label>
              </div>
              
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                  Sailing Date
                </label>
                <input
                  type="date"
                  name="sailingDate"
                  value={formData.sailingDate}
                  onChange={handleChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Remarks
              </label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows="3"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          {/* Shipping Terms */}
          <div style={{ 
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#374151' }}>
              Shipping Terms
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Port of Loading
              </label>
              <input
                type="text"
                name="portOfLoading"
                value={formData.portOfLoading}
                onChange={handleChange}
                placeholder="e.g., Incheon Airport, Republic of Korea"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Payment Terms and Terms of Price are auto-set by Shipping Origin - removed from inputs */}
          </div>

          {/* Invoice Settings */}
          <div style={{ 
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#374151' }}>
              Invoice Settings
            </h3>
            
            {/* Origin is auto-set by Shipping Origin - removed from inputs */}

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Shipment Terms
              </label>
              <input
                type="text"
                name="shipmentTerms"
                value={formData.shipmentTerms}
                onChange={handleChange}
                placeholder="e.g., Within 2 weeks after receipt of the payment"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Packing Terms
              </label>
              <input
                type="text"
                name="packingTerms"
                value={formData.packingTerms}
                onChange={handleChange}
                placeholder="e.g., Exportable standard packing"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Validity Period
              </label>
              <input
                type="text"
                name="validity"
                value={formData.validity}
                onChange={handleChange}
                placeholder="e.g., 3 weeks from Invoice date"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Bank Details */}
          <div style={{ 
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#374151' }}>
              Bank Details
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Bank Name
              </label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                placeholder="e.g., SHINHAN BANK (GASANDIGITAL BANKING CENTER)"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Bank Address
              </label>
              <input
                type="text"
                name="bankAddress"
                value={formData.bankAddress}
                onChange={handleChange}
                placeholder="e.g., 130, Digital-ro, Geumcheon-gu, Seoul, Korea"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                SWIFT Code
              </label>
              <input
                type="text"
                name="swiftCode"
                value={formData.swiftCode}
                onChange={handleChange}
                placeholder="e.g., SHBKKRSE"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Account Name
              </label>
              <input
                type="text"
                name="accountName"
                value={formData.accountName}
                onChange={handleChange}
                placeholder="e.g., PITTASOFT"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Account Number
              </label>
              <input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                placeholder="e.g., 180-007-8337750"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Remittance Note
              </label>
              <input
                type="text"
                name="remittanceNote"
                value={formData.remittanceNote}
                onChange={handleChange}
                placeholder="e.g., All remittance charge must be paid by the sender"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Signature Settings */}
          <div style={{ 
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#374151' }}>
              Signature Settings
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                Closing Phrase
              </label>
              <input
                type="text"
                name="closingPhrase"
                value={formData.closingPhrase}
                onChange={handleChange}
                placeholder="e.g., Your Very Truly,"
                style={{ 
                  width: '100%', 
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Company Signature is auto-set by Shipping Origin - removed from inputs */}
          </div>

          {/* Store Configuration - Only show in API Mode */}
          {useApiMode && (
            <div style={{ 
              backgroundColor: 'white',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#374151' }}>
                🔌 Store Configuration
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
                  (Required for API Mode)
                </span>
              </h3>
              
              {/* Store URL - Full Width */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                  Store URL *
                </label>
                <input
                  type="text"
                  name="storeUrl"
                  value={formData.storeUrl}
                  onChange={handleChange}
                  placeholder="https://your-store.myshopify.com"
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                  Enter your full Shopify store URL (e.g., https://blackvue-direct.myshopify.com)
                </div>
              </div>
              
              {/* Access Token - Full Width */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                  Access Token *
                </label>
                <input
                  type="password"
                  name="accessToken"
                  value={formData.accessToken}
                  onChange={handleChange}
                  placeholder="shpat_..."
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Fixed Bottom Section */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          flexShrink: 0
        }}>
          <button
            onClick={generatePayload}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#111827',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#1f2937'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#111827'}
          >
            Generate Document
          </button>

          {showPayload && (
            <div style={{ 
              marginTop: '20px',
              backgroundColor: 'white',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#374151' }}>
                Encrypted Payload
              </h3>
              <div style={{ 
                backgroundColor: '#f3f4f6',
                padding: '10px',
                borderRadius: '4px',
                wordBreak: 'break-all',
                fontSize: '12px',
                fontFamily: 'monospace',
                marginBottom: '10px'
              }}>
                {encryptedPayload}
              </div>
              <button
                onClick={() => copyToClipboard(encryptedPayload)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}
              >
                Copy Payload
              </button>
              <button
                onClick={() => copyToClipboard(iframeUrl)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Copy URL
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Preview */}
      <div style={{ 
        width: '55%', 
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ 
          padding: '20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <h2 style={{ margin: '0', fontSize: '18px', color: '#111827' }}>
            📄 Document Preview
          </h2>
          {iframeUrl && (
            <div style={{ 
              marginTop: '10px',
              fontSize: '12px',
              color: '#6b7280',
              wordBreak: 'break-all'
            }}>
              URL: {iframeUrl}
            </div>
          )}
        </div>
        
        <div style={{ flex: 1, position: 'relative', backgroundColor: '#f3f4f6' }}>
          {documentStatus === 'idle' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#9ca3af',
              fontSize: '16px',
              textAlign: 'center',
              padding: '20px'
            }}>
              📝 Configure settings and click Generate Document to preview
            </div>
          )}
          
          {documentStatus === 'loading' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#6b7280',
              fontSize: '16px',
              textAlign: 'center',
              flexDirection: 'column'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #e5e7eb',
                borderTop: '3px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '16px'
              }}></div>
              <div>🔄 Generating document...</div>
              {useApiMode && <div style={{ fontSize: '14px', marginTop: '8px', color: '#9ca3af' }}>Fetching data from Shopify API</div>}
            </div>
          )}
          
          {documentStatus === 'error' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#dc2626',
              fontSize: '16px',
              textAlign: 'center',
              padding: '20px',
              flexDirection: 'column'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Document Generation Failed</div>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                {errorMessage || 'An error occurred while generating the document'}
              </div>
              <button
                onClick={() => {
                  setDocumentStatus('idle');
                  setIframeUrl('');
                  setErrorMessage('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Try Again
              </button>
            </div>
          )}
          
          {(documentStatus === 'loading' || documentStatus === 'success') && iframeUrl && (
            <iframe
              src={iframeUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'white',
                display: documentStatus === 'loading' ? 'none' : 'block'
              }}
              title="Document Preview"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default PayloadBuilder;